import { httpClient } from 'shared/services/http/httpClient';
import { eventBuilder, EventModel, EventResourceDto } from '../models/Evento';

export interface EventsPageResult {
  data: EventModel[];
  meta: {
    total: number;
    current_page: number;
    last_page: number;
  };
}

export interface EventReferentPayload {
  name: string;
  role_id: number;
  email?: string;
  phone?: string;
}

export interface CreateEventPayload {
  name: string;
  description?: string;
  business_client_id: number;
  from_date: string;
  to_date: string;
  is_active: boolean;
  referents: EventReferentPayload[];
}

export interface UpdateEventPayload {
  name: string;
  description?: string;
  business_client_id: number;
  from_date?: string;
  to_date?: string | null;
  is_active: boolean;
}

export interface CreateSimpleEventPayload {
  business_client_id: number;
  name: string;
}

export type EventFileKind = 'signage' | 'budget';

export interface EventFilePayload {
  name: string;
  observacion?: string;
  url?: string;
}

// Algunos endpoints de Eventos no envuelven la respuesta en `{ data: ... }`
// tal como indica la documentación del backend (se vio en `POST /events`) —
// se acepta cualquiera de las dos formas y se falla con un mensaje claro si
// ninguna trae un evento real, en vez del típico
// "Cannot read properties of undefined" sin contexto.
const unwrapEventResource = (res: unknown): EventResourceDto => {
  const dto = (res as { data?: EventResourceDto })?.data ?? res;
  if (!dto || typeof (dto as EventResourceDto).id === 'undefined') {
    throw new Error(
      'La respuesta del servidor no tiene el formato esperado para un evento.'
    );
  }
  return dto as EventResourceDto;
};

const useEventoService = () => {
  const getEvents = async (
    page: number,
    perPage: number,
    businessClientId: number | null,
    fromDate: string | null,
    toDate: string | null,
    search: string | null
  ): Promise<EventsPageResult> => {
    const res = await httpClient.get('events', {
      params: {
        page,
        per_page: perPage,
        business_client_id: businessClientId ?? undefined,
        from_date: fromDate ?? undefined,
        to_date: toDate ?? undefined,
        search: search ?? undefined
      }
    });
    return {
      data: (res.data as EventResourceDto[]).map(eventBuilder.fromBackend),
      meta: {
        total: res.meta?.total ?? 0,
        current_page: res.meta?.current_page ?? 0,
        last_page: res.meta?.last_page ?? 0
      }
    };
  };

  const getEventById = async (id: number): Promise<EventModel> => {
    const res = await httpClient.get(`events/${id}`);
    return eventBuilder.fromBackend(unwrapEventResource(res));
  };


  const getEventActives = async (
    page: number,
    perPage: number,
    businessClientId: number | null,
    fromDate: string | null,
    toDate: string | null,
    search: string | null,
    clientId?: number | null,
  ): Promise<EventModel[]> => {
    const res = await httpClient.get('events/active', {
      params: {
        page,
        per_page: perPage,
        business_client_id: businessClientId ?? undefined,
        from_date: fromDate ?? undefined,
        to_date: toDate ?? undefined,
        search: search ?? undefined,
        client_id: clientId ?? undefined,
      }
    });
    return (res as EventResourceDto[]).map(eventBuilder.fromBackend);
  };

  const createEvent = async (
    payload: CreateEventPayload
  ): Promise<EventModel> => {
    const res = await httpClient.post('events', payload);
    return eventBuilder.fromBackend(unwrapEventResource(res));
  };

  const createSimpleEvent = async (
    payload: CreateSimpleEventPayload
  ): Promise<EventModel> => {
    const res = await httpClient.post('events/simple', payload);
    return eventBuilder.fromBackend(unwrapEventResource(res));
  };

  // A partir de acá, ninguno de estos endpoints de escritura se usa para
  // "leer" el evento actualizado — no se confía en que su respuesta
  // incluya siempre todas las relaciones (se vio en la práctica que el PUT
  // de datos generales no siempre trae `observations`, y el POST de
  // observaciones no siempre trae la observación recién creada). El hook
  // (`useEventoDetail`) refetchea el evento completo con `getEventById`
  // después de cada una de estas llamadas, así que alcanza con confirmar
  // que la petición HTTP no haya fallado.

  const updateEvent = async (
    id: number,
    payload: UpdateEventPayload
  ): Promise<void> => {
    await httpClient.put(`events/${id}`, payload);
  };

  const deleteEvent = async (id: number): Promise<void> => {
    await httpClient.delete(`events/${id}`);
  };

  const addReferent = async (
    eventId: number,
    payload: EventReferentPayload
  ): Promise<void> => {
    await httpClient.post(`events/${eventId}/referents`, payload);
  };

  const updateReferent = async (
    eventId: number,
    referentId: number,
    payload: EventReferentPayload
  ): Promise<void> => {
    await httpClient.put(`events/${eventId}/referents/${referentId}`, payload);
  };

  const removeReferent = async (
    eventId: number,
    referentId: number
  ): Promise<void> => {
    await httpClient.delete(`events/${eventId}/referents/${referentId}`);
  };

  const addObservation = async (
    eventId: number,
    observation: string
  ): Promise<void> => {
    await httpClient.post(`events/${eventId}/observations`, { observation });
  };

  const updateObservation = async (eventId: number, observationId: number, observation: string): Promise<void> => {
    await httpClient.put(`events/${eventId}/observations/${observationId}`, { observation });
  };

  const removeObservation = async (eventId: number, observationId: number): Promise<void> => {
    await httpClient.delete(`events/${eventId}/observations/${observationId}`);
  };

  // Paso 1 del flujo de adjuntar un archivo: sube a S3 y devuelve la url
  // pública. Esa url recién se persiste en el evento en el paso 2
  // (addSignage/addBudget), tal como lo pide el backend.
  const uploadFile = async (
    eventId: number,
    file: File,
    type: EventFileKind
  ): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const res = await httpClient.post(`events/${eventId}/upload`, formData);
    return (res as { url: string }).url;
  };

  const addSignage = async (
    eventId: number,
    payload: EventFilePayload
  ): Promise<void> => {
    await httpClient.post(`events/${eventId}/signages`, payload);
  };

  const updateSignage = async (
    eventId: number,
    signageId: number,
    payload: EventFilePayload
  ): Promise<void> => {
    await httpClient.put(`events/${eventId}/signages/${signageId}`, payload);
  };

  const removeSignage = async (
    eventId: number,
    signageId: number
  ): Promise<void> => {
    await httpClient.delete(`events/${eventId}/signages/${signageId}`);
  };

  const addBudget = async (
    eventId: number,
    payload: EventFilePayload
  ): Promise<void> => {
    await httpClient.post(`events/${eventId}/budgets`, payload);
  };

  const updateBudget = async (
    eventId: number,
    budgetId: number,
    payload: EventFilePayload
  ): Promise<void> => {
    await httpClient.put(`events/${eventId}/budgets/${budgetId}`, payload);
  };

  const removeBudget = async (
    eventId: number,
    budgetId: number
  ): Promise<void> => {
    await httpClient.delete(`events/${eventId}/budgets/${budgetId}`);
  };

  return {
    getEvents,
    getEventActives,
    getEventById,
    createEvent,
    createSimpleEvent,
    updateEvent,
    deleteEvent,
    addReferent,
    updateReferent,
    removeReferent,
    addObservation,
    updateObservation,
    removeObservation,
    uploadFile,
    addSignage,
    updateSignage,
    removeSignage,
    addBudget,
    updateBudget,
    removeBudget
  };
};

export default useEventoService;
